import { StorageKeys } from "@/enums/storageKeys.enum";
import axios from "axios";
import store from "./../store";
import { Endpoints } from "@/enums/endpoints.enum";
import toastService from "@/services/toast.service";
import errorReportingService from "@/services/errorReporting.service";

declare module "axios" {
  interface AxiosRequestConfig {
    skipErrorModalStatuses?: number[];
  }
}

const API_BASE = import.meta.env.VITE_API_URL || "https://hermod.zid.dev/";

axios.defaults.baseURL = `${API_BASE}v1`;
axios.defaults.headers.post["Content-Type"] = "application/json";
axios.defaults.headers.put["Content-Type"] = "application/json";
axios.defaults.withCredentials = true;
axios.defaults.headers.common["Accept-Language"] =
  localStorage.getItem(StorageKeys.lang) || "en";

let isLoggingOut = false;

function requestInterceptor(config: any) {
  const user = store.state.user;
  if (config.skipInterceptor) return config;
  else if (user) {
    config.headers["X-OFFICER-TOKEN"] = user.accessToken;
    delete config.headers.Authorization;
  } else {
    if (!isLoggingOut) {
      isLoggingOut = true;
      store.dispatch("removeUser");
      window.location.href = "/login";
    }
  }
  return config;
}

function responseErrorInterceptor(error: any) {
  // Skip reporting when the request never produced a response: aborted
  // (debounced search cancelling a previous in-flight call), network failure
  // (offline / DNS / connection reset), or timeout. These all yield an
  // `error.response === undefined` and would surface in the modal as a
  // useless empty payload (no status, no headers, no body).
  if (
    axios.isCancel?.(error) ||
    error?.name === "CanceledError" ||
    error?.code === "ERR_CANCELED" ||
    error?.code === "ERR_NETWORK" ||
    error?.code === "ECONNABORTED" ||
    !error?.response
  ) {
    return Promise.reject(error);
  }

  const status = error?.response?.status;
  const code = error?.response?.data?.message?.code;

  if (status === 401 && code === "ERROR_SESSION_INVALID" && !isLoggingOut) {
    isLoggingOut = true;

    toastService.ShowError("Session expired. Redirecting to login");

    setTimeout(() => {
      store.dispatch("removeUser");
      window.location.href = "/login";
    }, 500);

    return Promise.reject(error);
  }

  // TODO: remove when the old backoffice thing is solved - PATCH START
  if (
    error?.request?.responseURL?.includes(Endpoints.UsersCreate) ||
    error?.request?.responseURL?.includes(Endpoints.RolesCreate)
  )
    return Promise.reject(error);
  //PATCH END

  // Keep recoverable failures rejected so the caller can run its fallback.
  if (
    error?.config?.skipErrorModal === true ||
    error?.config?.skipErrorModalStatuses?.includes(status)
  ) {
    return Promise.reject(error);
  }

  // Statuses that are expected outcomes, not engineering bugs:
  //   401 — auth/session issue (the session-expired path is handled above)
  //   403 — caller lacks permission; nothing the user reporting it can do
  // Reporting these floods the modal with noise that staff can't act on.
  // 422 (validation) is intentionally NOT skipped — surfaced so we can spot
  // FE/BE schema mismatches.
  if (status === 401 || status === 403) {
    return Promise.reject(error);
  }

  errorReportingService.reportError(error);
  return Promise.reject(error);
}

//HTTP request interceptor
axios.interceptors.request.use(requestInterceptor);

// HTTP response interceptor
axios.interceptors.response.use((response) => response, responseErrorInterceptor);

// v2 axios instance (same interceptors, different base URL)
export const axiosV2 = axios.create({
  baseURL: `${API_BASE}v2`,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});
axiosV2.interceptors.request.use(requestInterceptor);
axiosV2.interceptors.response.use((response) => response, responseErrorInterceptor);

export default axios;
